import axios from 'axios'
import _ from 'lodash'
import * as types from '../mutation-types'
import { checkAvailableSchema } from './menu'
import { checkViolatePromoCondition, checkViolatePromoItemCondition, getViolatePromoConditionMessage } from './cart'

// namespaced
export const namespaced = true

// state
export const state = {
  msg: null,
  promoItems: [],
  activePromo: null
}

// getters
export const getters = {
  promoMsg: state => state.msg,
  promoItems: (state, getters, rootState) => {
    const pItems = []
    state.promoItems.forEach(p => {
      const promoSetting = p.promo_setting
      // Skip when suggest_type is never
      if (parseInt(promoSetting.suggest_type) === 0) return
      // Upon not added
      if (parseInt(promoSetting.suggest_type) === 1) {
        if (!rootState.cart.cart.find(c => parseInt(c.plu_id) === parseInt(p.plu_id))) pItems.push(p)
      // Upon category not added
      } else {
        if (!rootState.cart.cart.find(c => parseInt(c.category_id) === parseInt(p.category_id))) pItems.push(p)
      }
    })
    return pItems
  },
  activePromo: state => state.activePromo
}

// mutations
export const mutations = {
  [types.RESET_PROMO_DATA] (state) {
    Object.assign(state, {
      msg: null,
      promoItems: [],
      activePromo: null
    })
  },

  [types.SET_PROMO_MSG] (state, msg) {
    const msgs = {
      promo: 'Failed to fetch promotion data',
      promo2: 'Failed to save promotion data',
      promo3: 'Promo code is invalid. Please make sure that you have entered the correct code and check the terms & conditions.',
      promo4: 'Promo code could not be applied because the condition is not fulfilled. Kindly check the terms & conditions and try again.'
    }
    const message = msg ? (msgs[msg] ? msgs[msg] : msg) : 'Failed to fetch data'
    state.msg = message
  },

  [types.RESET_PROMO_MSG] (state) {
    state.msg = null
  },

  [types.SET_PROMO_ITEMS] (state, payload) {
    state.promoItems = payload
  },

  [types.RESET_PROMO_ITEMS] (state) {
    state.promoItems = []
  },

  [types.SET_ACTIVE_PROMO] (state, payload) {
    state.activePromo = payload
  },

  [types.RESET_ACTIVE_PROMO] (state) {
    state.activePromo = null
  }
}

// actions
export const actions = {
  resetPromoData ({ commit }) {
    commit(types.RESET_PROMO_DATA)
  },

  async fetchPromoItems ({ commit, rootGetters }) {
    try {
      const { data } = await axios.post('/api/promo_items', {
        restaurant_id: parseInt(rootGetters['config/rid']) === 0 ? parseInt(rootGetters['config/rid2']) : parseInt(rootGetters['config/rid'])
      })
      const itemList = []
      const timeData = await axios.post('/api/moment/current', {
        holidays: rootGetters['config/holidays']
      })
      const currentTime = timeData.data
      data.forEach(c => {
        if (checkAvailableSchema(c.avail_schema, currentTime) === 0) itemList.push(c)
      })
      if (itemList && itemList.length > 0) {
        const items = itemList.map(item => {
          if ((item.courses && item.courses.length > 0) || (item.option_groups && item.option_groups.length > 0)) {
            const item2 = item
            if (item.courses && item.courses.length > 0) {
              const courses = item.courses.map(course => {
                let reqLen
                let selected
                let selector
                let multiple
                const maxLen = course.maximum_choice
                if (course.minimum_choice === 0) {
                  reqLen = null
                  selector = 'checkbox'
                  selected = []
                  if (maxLen === 1) {
                    multiple = false
                  } else {
                    multiple = true
                  }
                } else {
                  reqLen = course.minimum_choice
                  if (maxLen === 1) {
                    selector = 'radio'
                    selected = course.course_items.length === 1 ? course.course_items[0].item_id : null
                    multiple = false
                  } else {
                    selector = 'checkbox'
                    selected = []
                    multiple = true
                  }
                }
                return {
                  ...course,
                  selector: selector,
                  selected: selected,
                  req_len: reqLen,
                  max_len: maxLen,
                  multiple: multiple
                }
              })
              item2.courses = courses
            }
            if (item.option_groups && item.option_groups.length > 0) {
              const options = item.option_groups.map(option => ({
                ...option,
                selector: (option.is_multiple === 1) ? 'checkbox' : 'radio',
                selected: (option.is_multiple === 1) ? [] : null,
                req_len: (option.is_optional === 0 && option.is_multiple === 0) ? 1 : null
              }))
              item2.option_groups = options
            }
            return item2
          }
          return item
        })
        commit(types.SET_PROMO_ITEMS, items)
      } else {
        commit(types.RESET_PROMO_ITEMS)
      }
    } catch (e) {
      commit(types.RESET_PROMO_ITEMS)
    }
  },

  resetPromoItems ({ commit }) {
    commit(types.RESET_PROMO_ITEMS)
  },

  async fetchActivePromo ({ commit, dispatch, rootState, rootGetters }, payload) {
    commit(types.RESET_PROMO_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      const { data } = await axios.post('/api/promo_code', {
        restaurant_id: parseInt(rootGetters['config/rid']),
        promo_code: payload,
        pickup_datetime: timeData.data
      })
      if (data) {
        if (data.item) {
          const items = rootGetters['cart/cart'].filter(i => {
            return i.is_promo && data.item.plu_id === i.plu_id
          }).map(i => i.quantity)
          const itemQty = items.length > 0 ? (items.reduce((a, b) => a + b)) + 1 : 1
          if (!checkViolatePromoCondition(data.item.promo_setting, rootGetters['config/mode'].dineMode, rootGetters['member/isMember'], rootGetters['cart/cartAmount']) &&
            !checkViolatePromoItemCondition(data.item, itemQty)
          ) {
            if (data.item.courses && data.item.courses.length > 0) {
              data.item.is_promo = true
              if (data.item.price_override) {
                data.item.final_price = parseFloat(data.item.price_override)
              }
              dispatch('menu/showMenuItemDialog', data.item, { root: true })
            } else {
              dispatch('cart/addCartItem', {
                item: JSON.parse(JSON.stringify(data.item)),
                quantity: 1,
                final_price: parseFloat(data.item.price_override || data.item.price),
                is_promo: true
              }, { root: true })
            }
          } else {
            commit(types.RESET_ACTIVE_PROMO)
            let invalidMsg = getViolatePromoConditionMessage(data.item.promo_setting, rootGetters['config/mode'].dineMode, rootGetters['member/isMember'], rootGetters['cart/cartAmount'])
            commit(types.SET_PROMO_MSG, invalidMsg)
          }
        } else {
          const presetDiscount = data.preset_discount
          if (!checkViolatePromoCondition(presetDiscount.promo_setting, rootGetters['config/mode'].dineMode, rootGetters['member/isMember'], rootGetters['cart/cartAmount'])) {
            const modeData = rootState.config.mode
            if (modeData.hasPresetDiscount && (parseFloat(modeData.presetDiscount.discount_amount) >= parseFloat(presetDiscount.Amount || 0))) {
              commit(types.RESET_ACTIVE_PROMO)
              commit(types.SET_PROMO_MSG, 'The default discount provides greater or equal discount value than the applied promo code, hence the promo code will not be applied.')
              // current promo code is not applied, so change iorder_promo_code status from pending to available, if any
              if (presetDiscount.promo_setting.iorder_promo_code_id) {
                dispatch('updatePromoCodeStatusToAvailable', presetDiscount.promo_setting.iorder_promo_code_id)
              }
            } else {
              const discountType = parseInt(presetDiscount.Type) === 1 ? 'Price' : 'Percentage'
              // change previous iorder_promo_code status pending to available, if any
              if (rootGetters['cart/discountInfo'].iorder_promo_code_id && rootGetters['cart/discountInfo'].iorder_promo_code_id !== presetDiscount.promo_setting.iorder_promo_code_id) {
                dispatch('updatePromoCodeStatusToAvailable', rootGetters['cart/discountInfo'].iorder_promo_code_id)
              }
              dispatch('cart/setDiscountInfo', {
                discount_id: presetDiscount.ID,
                discount_name: presetDiscount.Name,
                discount_amount: parseFloat(presetDiscount.Amount),
                promo_code: presetDiscount.promo_setting.promo_code,
                discount_type: discountType,
                iorder_promo_discount_id: presetDiscount.promo_setting.id,
                iorder_promo_code_id: presetDiscount.promo_setting.iorder_promo_code_id
              }, { root: true })
            }
          } else {
            commit(types.RESET_ACTIVE_PROMO)
            let invalidMsg = getViolatePromoConditionMessage(presetDiscount.promo_setting, rootGetters['config/mode'].dineMode, rootGetters['member/isMember'], rootGetters['cart/cartAmount'])
            commit(types.SET_PROMO_MSG, invalidMsg)
          }
        }
      } else {
        commit(types.RESET_ACTIVE_PROMO)
        commit(types.SET_PROMO_MSG, 'promo3')
      }
    } catch (e) {
      commit(types.RESET_ACTIVE_PROMO)
      commit(types.SET_PROMO_MSG, 'promo3')
    }
  },
  updatePromoCodeStatusToAvailable ({ commit }, payload) {
    axios.post('/api/promo_code/update', {
      promo_code_id: payload
    })
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/promo.js